import { z } from 'zod';

// Structured analysis response for the Life Rebuild Score assessment.
//
// This is a superset of the StandardAnalysis fields (category / score / insights)
// so the email orchestrator's profile enrichment and any generic fallback keep
// working, plus the archetype-specific structure the custom result page renders.
//
// The happy path uses outputFormat 'json' + generateObject, which fills this
// schema directly. parseLifeRebuildAnalysis below is a defensive fallback for
// the rare case a result arrives as raw markdown text.

export type RebuildDomain =
  | 'Routines'
  | 'Health'
  | 'Money'
  | 'Career'
  | 'Relationships'
  | 'Identity';

export interface LifeRebuildDomainRead {
  domain: string; // one of RebuildDomain, kept as string for generation tolerance
  read: string;
}

export interface LifeRebuildScoreAnalysisResponse {
  // Archetype name, e.g. "The Routine Collapser". Doubles as the StandardAnalysis
  // `category` consumed by the email orchestrator and generic results component.
  category: string;
  // 0-100 readiness score (lower = more rebuilding ahead). Compat with StandardAnalysis.
  score: number;
  // One-paragraph diagnosis reflected back in the user's own answers (free section).
  diagnosis: string;
  // Per-domain one-line reads across the 6 rebuild domains (free section).
  domains: LifeRebuildDomainRead[];
  // Short standalone takeaways. Mirrors StandardAnalysis `insights` for email compat.
  insights: string[];
  // The single highest-leverage first move for this archetype (gated section).
  rebuildFirst: {
    move: string;
    detail: string;
  };
  // Forward-looking tease of the full rebuild sequence + week-1 plan (gated section).
  whatComesNext: string;
}

export const LifeRebuildScoreAnalysisResponseSchema = z.object({
  category: z
    .string()
    .describe(
      'The archetype name, exactly one of: The Routine Collapser, The Body Wake-Up, The Money Reckoning, The Career Pivot, The Identity Recompose'
    ),
  score: z
    .number()
    .min(0)
    .max(100)
    .describe(
      'A 0-100 Life Rebuild readiness score. Lower means more rebuilding is ahead; higher means closer to aligned.'
    ),
  diagnosis: z
    .string()
    .describe(
      "One paragraph, 3-4 sentences, calm and direct, reflecting the user's own answers back and ending on a canonical reframe line."
    ),
  domains: z
    .array(
      z.object({
        domain: z
          .string()
          .describe('One of: Routines, Health, Money, Career, Relationships, Identity'),
        read: z.string().describe("A one-line read on this domain using the user's answer."),
      })
    )
    .min(3)
    .max(6)
    .describe('A one-line read for each of the 6 rebuild domains, in order.'),
  insights: z
    .array(z.string())
    .min(3)
    .max(6)
    .describe('3-6 short standalone takeaways drawn from the domain reads.'),
  rebuildFirst: z.object({
    move: z.string().describe('A single-sentence first move for this archetype.'),
    detail: z
      .string()
      .describe('2-3 sentences expanding the first move. Specific, calm, no hustle.'),
  }),
  whatComesNext: z
    .string()
    .describe('2 sentences previewing the full rebuild sequence and the day-by-day first week.'),
});

const FALLBACK: LifeRebuildScoreAnalysisResponse = {
  category: 'Your Life Rebuild',
  score: 50,
  diagnosis:
    'You are at the rebuild. The full read across your six domains is ready below, with the one place to start first.',
  domains: [],
  insights: [],
  rebuildFirst: {
    move: 'Defend one anchor routine for fourteen days before touching anything else.',
    detail:
      'Pick the single rhythm that steadies the rest of your week and protect it first. One system at a time, in the right order.',
  },
  whatComesNext:
    'Your full rebuild sequence and a day-by-day first week are ready. Unlock them to start the rebuild in the right order.',
};

/**
 * Defensive fallback parser for the rare case the analysis arrives as raw markdown
 * (legacy runs) instead of the structured object. Mirrors the personalized-goal-plan
 * pattern. The happy path (json + generateObject) does not use this.
 */
export function parseLifeRebuildAnalysis(raw: string): LifeRebuildScoreAnalysisResponse {
  if (!raw) return { ...FALLBACK };

  const text = raw.replace(/<[^>]*>/g, '\n').replace(/\r/g, '');

  const section = (heading: RegExp): string => {
    const lines = text.split('\n');
    const startIdx = lines.findIndex((l) => heading.test(l));
    if (startIdx === -1) return '';
    const body: string[] = [];
    for (let i = startIdx + 1; i < lines.length; i++) {
      if (/^\s*#{1,6}\s/.test(lines[i])) break;
      body.push(lines[i]);
    }
    return body.join('\n').trim();
  };

  // "## You are in a The Routine Collapser" or "## You are in The Routine Collapser"
  const archetypeLine = text
    .split('\n')
    .find((l) => /you are (in|an?)/i.test(l) && /^\s*#{1,6}/.test(l));
  const category =
    archetypeLine
      ?.replace(/^\s*#{1,6}\s*/, '')
      .replace(/^you are (in a|in an|in|an?)\s*/i, '')
      .trim() || FALLBACK.category;

  const diagnosis = section(/you are (in|an?)/i) || FALLBACK.diagnosis;

  const domainBlock = section(/6 domains|six domains|rebuild score across/i);
  const domains: LifeRebuildDomainRead[] = domainBlock
    .split('\n')
    .map((l) => l.replace(/^[-*•]\s*/, '').trim())
    .filter(Boolean)
    .map((l) => {
      const m = l.match(/^\*{0,2}([^:*]+)\*{0,2}\s*:\s*(.+)$/);
      return m ? { domain: m[1].trim(), read: m[2].trim() } : { domain: '', read: l };
    })
    .filter((d) => d.read);

  const rebuildBlock = section(/rebuild first/i);
  const moveLine = text.split('\n').find((l) => /rebuild first/i.test(l));
  const move =
    moveLine
      ?.replace(/^\s*#{1,6}\s*/, '')
      .replace(/rebuild first\s*:?\s*/i, '')
      .trim() || FALLBACK.rebuildFirst.move;

  const whatComesNext = section(/what comes next/i) || FALLBACK.whatComesNext;

  return {
    category,
    score: FALLBACK.score,
    diagnosis,
    domains: domains.length ? domains : FALLBACK.domains,
    insights: domains.slice(0, 6).map((d) => `${d.domain}: ${d.read}`),
    rebuildFirst: {
      move,
      detail: rebuildBlock || FALLBACK.rebuildFirst.detail,
    },
    whatComesNext,
  };
}
